import { ArrowLeft, ArrowRight, ArrowUpRight, Check, ChevronDown, X } from "lucide-react";
import posthog from "posthog-js";
import { useEffect, useMemo, useRef, useState } from "react";
import { Badge } from "@/app/components/ui/badge";
import { Button } from "@/app/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/app/components/ui/tabs";
import { cn } from "@/app/handlers/cn.utils";
import { AnswerValue, AssessedCategory, AssessedItem, FitAnswer, FitAssessment } from "../config/api";
import { missQuestions, requiredStats } from "../config/stats";

interface FitReportProps {
	assessment: FitAssessment;
	answers: FitAnswer[];
	onAnswersChange: (answers: FitAnswer[]) => void;
	onComplete: (answers: FitAnswer[]) => void;
	onChangeResume: () => void;
	onUpdateCv: () => void;
	jobId: string;
	jobTitle: string;
}

type TabKey = "overview" | "questions" | "plan";

const CATEGORIES: { key: keyof FitAssessment; label: string }[] = [
	{ key: "experience", label: "Work experience" },
	{ key: "skills", label: "Skills" },
	{ key: "education", label: "Education" },
	{ key: "certifications", label: "Certifications" },
];

const CATEGORY_LABELS: Record<string, string> = {
	experience: "Work experience",
	skills: "Skills",
	education: "Education",
	certifications: "Certifications",
};

const SEG_CLASS = {
	fit: "bg-green-400",
	have: "bg-amber-400",
	miss: "bg-red-400",
	gray: "bg-gray-200",
} as const;

function requiredStatsFor(items: AssessedItem[]) {
	const required = items.filter((i) => i.necessity === "required");
	return { met: required.filter((i) => i.status === "Fit").length, total: required.length };
}

function planHeadline(fixable: number, missing: number) {
	if (fixable > 0 && missing === 0) {
		return {
			title:
				fixable === 1
					? "One quick edit and you are a stronger match"
					: `${fixable} quick edits and you are a stronger match`,
			subtitle: "Everything this role asks for, you have. It just needs to show on your CV.",
		};
	}
	if (fixable > 0) {
		return {
			title: `${fixable} quick ${fixable === 1 ? "win" : "wins"}, ${missing} to build toward`,
			subtitle: "Some gaps are just missing from your CV. The rest are real, and worth knowing before you apply.",
		};
	}
	if (missing > 0) {
		return {
			title: "Here is what to build toward",
			subtitle: "These gaps are real. Knowing them now is half the battle.",
		};
	}
	return {
		title: "You already show everything they asked for",
		subtitle: "No gaps to close on your CV for this role.",
	};
}

function CategoryRow({ label, category }: { label: string; category: AssessedCategory }) {
	const [open, setOpen] = useState(false);
	const { met, total } = requiredStatsFor(category.items);
	const pct = total > 0 ? Math.round((met / total) * 100) : 0;

	return (
		<div className="border-t border-gray-200 first:border-t-0 font-inter">
			<button
				onClick={() => setOpen((v) => !v)}
				className="flex w-full items-center gap-4 rounded-md py-3.5 text-left transition-colors hover:bg-gray-50"
			>
				<span className="flex-1 text-[15px] font-medium text-gray-800">{label}</span>
				<div className="flex items-center gap-4">
					<p className="text-right text-sm text-nowrap font-medium text-gray-600 font-inter">
						{met} of {total}
					</p>
					<span className="hidden h-1.5 w-28 overflow-hidden rounded-full bg-gray-200 sm:block">
						<span
							className="block h-full rounded-full bg-green-500 transition-[width] duration-500"
							style={{ width: `${pct}%` }}
						/>
					</span>
					<ChevronDown className={cn("h-4 w-4 shrink-0 text-gray-700 transition-transform", open && "rotate-180")} />
				</div>
			</button>

			<div
				className={cn(
					"grid transition-[grid-template-rows] duration-300 ease-out motion-reduce:transition-none",
					open ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
				)}
			>
				<div className="overflow-hidden">
					<div className="p-3 pt-0">
						<p className="pb-2.5 text-sm text-gray-700">{category.summary}</p>
						<ul className="space-y-2">
							{category.items
								.filter((item) => item.necessity === "required")
								.map((item) => (
									<li key={item.item} className="flex items-start gap-3 text-sm text-gray-800">
										<span
											className={cn(
												"mt-0.5 flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full ring-1",
												item.status === "Fit"
													? "bg-green-100 text-green-700 ring-green-200"
													: "bg-gray-100 text-gray-400 ring-gray-200",
											)}
										>
											{item.status === "Fit" && <Check className="h-3 w-3" />}
										</span>
										<span>
											{item.item}
											{item.necessity === "preferred" && (
												<span className="ml-1.5 rounded bg-amber-100 px-1.5 py-px text-[10px] font-bold uppercase text-amber-700">
													pref
												</span>
											)}
										</span>
									</li>
								))}
						</ul>
					</div>
				</div>
			</div>
		</div>
	);
}

function FitReport({
	assessment,
	answers,
	onAnswersChange,
	onComplete,
	onChangeResume,
	onUpdateCv,
	jobId,
	jobTitle,
}: FitReportProps) {
	const questions = useMemo(() => missQuestions(assessment), [assessment]);
	const { met, total } = requiredStats(assessment);

	const [tab, setTab] = useState<TabKey>("overview");
	const [qi, setQi] = useState(0);

	const fixable = answers.filter((a) => a.answer === "have");
	const missing = answers.filter((a) => a.answer === "lack");
	const have = fixable.length;
	const lack = missing.length;
	const planReady = questions.length === 0 || answers.length >= questions.length;
	// "Completed" means the user actually worked through the questions — not the no-questions case,
	// which lands on Overview so an unopened check does not jump straight to a plan.
	const questionsAnswered = questions.length > 0 && answers.length >= questions.length;

	// Land a returning user on their finished plan. Answers seed asynchronously, so decide once they
	// arrive rather than at first render; the ref keeps a later manual tab change from being overridden.
	const didLandOnPlan = useRef(false);
	useEffect(() => {
		if (didLandOnPlan.current) return;
		if (questionsAnswered) {
			didLandOnPlan.current = true;
			setTab("plan");
		}
	}, [questionsAnswered]);

	// Cheap to derive during render; no useMemo (rerender-simple-expression-in-memo).
	const segments = Array.from({ length: total }, (_, i) =>
		i < met ? "fit" : i < met + have ? "have" : i < met + have + lack ? "miss" : "gray",
	);

	const visibleCategories = CATEGORIES.filter(({ key }) => assessment[key].items.length > 0);

	const goQuestions = () => {
		if (questions.length === 0) {
			onComplete([]);
			setTab("plan");
			return;
		}
		setQi(0);
		setTab("questions");
	};

	const answer = (value: AnswerValue) => {
		const q = questions[qi];
		const entry: FitAnswer = { item: q.item, category: q.category, question: q.question, answer: value };
		const next = [...answers.filter((a) => a.item !== q.item), entry];
		posthog.capture("fitcheck_question_answered", {
			jobId,
			jobTitle,
			item: q.item,
			category: q.category,
			answer: value,
			questionNumber: qi + 1,
			totalQuestions: questions.length,
		});
		onAnswersChange(next);
		if (qi + 1 < questions.length) {
			setQi(qi + 1);
		} else {
			onComplete(next);
			setTab("plan");
		}
	};

	const handleTabChange = (value: string) => {
		if (value === "questions") {
			setQi(0);
			setTab("questions");
		} else {
			setTab(value as TabKey);
		}
	};

	const caption =
		tab === "overview" ? (
			<>
				You show{" "}
				<b className="font-semibold text-primary-700">
					{met} of {total} must-haves
				</b>
				. Open a category to see where the gaps are.
			</>
		) : tab === "questions" ? (
			"A few quick questions to sort the real gaps from the fixable ones."
		) : (
			<>
				<span className="font-semibold text-gray-800">{met}</span> on your CV,{" "}
				<span className="font-semibold text-gray-800">{have}</span> to add,{" "}
				<span className="font-semibold text-gray-800">{lack}</span> to build toward.
			</>
		);

	const q = questions[qi];
	const { title, subtitle } = planHeadline(fixable.length, missing.length);

	return (
		<Tabs value={tab} onValueChange={handleTabChange} className="gap-0">
			<TabsList className="w-full justify-start gap-1 rounded-none border-b border-gray-200 bg-transparent p-0 px-5 pt-2 text-muted-foreground">
				<TabsTrigger
					value="overview"
					className="rounded-none border-b-2 border-transparent px-3 pb-3 pt-1 text-gray-500 data-[state=active]:border-primary-700 data-[state=active]:bg-transparent data-[state=active]:text-gray-900 data-[state=active]:shadow-none"
				>
					Overview
				</TabsTrigger>
				<TabsTrigger
					value="questions"
					disabled={questions.length === 0}
					className="gap-2 rounded-none border-b-2 border-transparent px-3 pb-3 pt-1 text-gray-500 data-[state=active]:border-primary-700 data-[state=active]:bg-transparent data-[state=active]:text-gray-900 data-[state=active]:shadow-none"
				>
					Questions
					<Badge variant="secondary" className="tabular-nums">
						{questions.length}
					</Badge>
				</TabsTrigger>
				<TabsTrigger
					value="plan"
					disabled={!planReady}
					className="rounded-none border-b-2 border-transparent px-3 pb-3 pt-1 text-gray-500 data-[state=active]:border-primary-700 data-[state=active]:bg-transparent data-[state=active]:text-gray-900 data-[state=active]:shadow-none"
				>
					Next steps
				</TabsTrigger>
			</TabsList>

			<div className="p-5 space-y-3">
				<p className="text-gray-700 text-sm font-inter">{caption}</p>
				<div className="flex gap-1">
					{segments.map((s, i) => (
						<span
							key={i}
							style={{ animationDelay: `${i * 35}ms` }}
							className={cn(
								"h-3 flex-1 rounded-[3px] transition-colors duration-300",
								"motion-safe:animate-fc-rise",
								SEG_CLASS[s],
							)}
						/>
					))}
				</div>
			</div>

			<TabsContent value="overview" className="px-5 motion-safe:animate-fc-fade">
				<div className="border-b border-gray-200">
					{visibleCategories.map(({ key, label }) => (
						<CategoryRow key={key} label={label} category={assessment[key]} />
					))}
				</div>
				<div className="my-5 flex items-center justify-between pt-4">
					<Button onClick={onChangeResume} title="Change resume" variant="secondary" size="sm">
						<ArrowLeft className="h-[18px] w-[18px]" />
					</Button>
					<Button size="sm" onClick={goQuestions}>
						Continue
						<ArrowRight className="h-4 w-4 ml-3" />
					</Button>
				</div>
			</TabsContent>

			<TabsContent value="questions" className="px-5 py-5 motion-safe:animate-fc-fade">
				{q && (
					<div key={qi} className="motion-safe:animate-fc-slide-right">
						<div className="mb-3 space-y-2">
							<div className="whitespace-nowrap text-[11px] font-semibold uppercase tracking-wider text-gray-500">
								Question {qi + 1} of {questions.length}
							</div>
							<Badge variant="secondary" className="uppercase tracking-wide">
								{CATEGORY_LABELS[q.category]}
							</Badge>
						</div>

						<p className="text-base font-medium leading-relaxed text-gray-900">{q.item}</p>
						<p className="text-sm text-gray-600">
							This role asks for it, but it is not on your CV. Which is closer to the truth?
						</p>

						<div className="mt-5 grid gap-3 sm:grid-cols-2">
							<button
								onClick={() => answer("have")}
								className="flex flex-col items-start rounded-lg border border-gray-200 p-4 text-left transition-all hover:border-primary-700 hover:bg-primary-50 active:scale-[0.98]"
							>
								<span className="mb-2.5 flex h-[30px] w-[30px] items-center justify-center rounded-lg bg-green-100 text-green-600">
									<Check className="h-4 w-4" />
								</span>
								<span className="text-sm font-semibold text-gray-900">I have it</span>
								<span className="mt-0.5 text-xs text-gray-600">Just not written on my CV yet</span>
							</button>
							<button
								onClick={() => answer("lack")}
								className="flex flex-col items-start rounded-lg border border-gray-200 p-4 text-left transition-all hover:border-primary-700 hover:bg-primary-50 active:scale-[0.98]"
							>
								<span className="mb-2.5 flex h-[30px] w-[30px] items-center justify-center rounded-lg bg-gray-100 text-gray-500">
									<X className="h-4 w-4" />
								</span>
								<span className="text-sm font-semibold text-gray-900">Not yet</span>
								<span className="mt-0.5 text-xs text-gray-600">I do not have this experience</span>
							</button>
						</div>
					</div>
				)}
			</TabsContent>

			<TabsContent value="plan" className="px-5 py-5 border-gray-200 border-t motion-safe:animate-fc-fade">
				<p className="font-semibold text-gray-800 font-inter">{title}</p>
				<p className="mt-1 text-sm text-gray-700 font-inter">{subtitle}</p>

				{fixable.length > 0 && (
					<section className="mt-6">
						<div className="mb-1 flex items-center gap-2.5">
							<h4 className="text-sm font-bold text-gray-900">Add to your CV</h4>
							<Badge className="bg-amber-100 tabular-nums text-amber-700">{fixable.length}</Badge>
						</div>
						<p className="text-[13px] text-gray-600">
							You have these. They are just not written down. Add them and this employer will see them.
						</p>
						<ul className="mt-3">
							{fixable.map((a, i) => (
								<li
									key={a.item}
									style={{ animationDelay: `${i * 50}ms` }}
									className="flex items-start gap-3 mb-3 text-sm text-gray-800 motion-safe:animate-fc-rise"
								>
									<span className="mt-0.5 flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700 ring-1 ring-amber-200">
										<Check className="h-3 w-3" />
									</span>
									<span>{a.item}</span>
								</li>
							))}
						</ul>
						<div className="my-6">
							<Button onClick={onUpdateCv} size="sm">
								Tailor my Resume
								<ArrowRight className="h-4 w-4 ml-2" />
							</Button>
						</div>
					</section>
				)}

				{missing.length > 0 && (
					<section className="mt-6">
						<div className="mb-1 flex items-center gap-2.5">
							<h4 className="text-sm font-bold text-gray-900">Build toward</h4>
							<Badge className="bg-red-100 tabular-nums text-red-700">{missing.length}</Badge>
						</div>
						<p className="text-[13px] text-gray-600">
							Genuine gaps. They take time to grow, so it is worth knowing before you invest in this application.
						</p>
						<ul className="mt-3">
							{missing.map((a, i) => (
								<li
									key={a.item}
									style={{ animationDelay: `${i * 50}ms` }}
									className="flex items-start gap-3 border-t border-gray-200 py-2.5 text-sm text-gray-800 first:border-t-0 motion-safe:animate-fc-rise"
								>
									<span className="mt-0.5 flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-red-100 text-red-700 ring-1 ring-red-200">
										<ArrowUpRight className="h-3 w-3" />
									</span>
									<span>{a.item}</span>
								</li>
							))}
						</ul>
					</section>
				)}
			</TabsContent>
		</Tabs>
	);
}

export default FitReport;
